import React, { useRef, useEffect } from 'react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import ChartBubble from '@intelligence/components/ChartBubble';
import useChartResize from '@gp/hooks/useChartResize';
import { getElementStyleMathValue } from '@gp/utils/common';
import { calcIntervalOfXAxisLabel } from '@gp/utils/echarts';
import { STACKED_AREA_CHART_OPTION } from './constants';

import type { AutoStackedAreaChartProps } from './types';

echarts.use([LineChart, TooltipComponent, GridComponent, LabelLayout, UniversalTransition, CanvasRenderer]);

const AutoStackedAreaChart: React.FC<AutoStackedAreaChartProps> = (props) => {
  const { data, categoryLabelFormatter, valueFormatter } = props;

  const chartNode = useRef<HTMLDivElement>(null);
  const chart = useRef<any>(null);

  useChartResize(chart);

  const getOptionWithData = (data: any[]) => {
    const maxValue = Math.max.apply(
      null,
      data?.map((item) => item?.value),
    );
    const intervalOfXAxisLabel = calcIntervalOfXAxisLabel({
      canvasWidth: getElementStyleMathValue(chart.current?.getDom(), 'width'),
      gridLeft: Number.parseFloat((STACKED_AREA_CHART_OPTION.grid as any)?.left),
      gridRight: Number.parseFloat((STACKED_AREA_CHART_OPTION.grid as any)?.right),
      labelWidth: 16,
      firstLabelWidth: 48,
      lastLabelWidth: 48,
      length: data?.length || 0,
    });

    return {
      ...STACKED_AREA_CHART_OPTION,
      tooltip: {
        ...STACKED_AREA_CHART_OPTION.tooltip,
        formatter: (params: any) => {
          const paramsTemp = [...params];
          paramsTemp[0].seriesName = 'Headcount';
          return ChartBubble({
            chartType: 'line',
            dateType: 'Month',
            headerLeftTitle: '-',
            headerRightTitle: '',
            data: paramsTemp,
            topChartsType: 'store_rank_more_line',
            valueFormatter,
          });
        },
      },
      xAxis: {
        ...STACKED_AREA_CHART_OPTION?.xAxis,
        axisLabel: {
          // @ts-ignore
          ...STACKED_AREA_CHART_OPTION?.xAxis?.axisLabel,
          formatter: (value: string, index: number): string => {
            if (!(index === 0 || index === data?.length - 1 || index % (intervalOfXAxisLabel + 1) === 0)) return '';
            if (categoryLabelFormatter instanceof Function) {
              return categoryLabelFormatter(value, index, data?.length);
            }
            return value;
          },
        },
        data: data?.map((item) => item?.category),
      },
      yAxis: {
        ...STACKED_AREA_CHART_OPTION?.yAxis,
        interval: Math.round(maxValue / 2),
      },
      series: [
        {
          // @ts-ignore
          ...STACKED_AREA_CHART_OPTION?.series?.[0],
          data: data?.map((item, index) => ({
            value: item?.value,
            symbol: 'circle',
            symbolSize: 8,
            // 节点的业务数据
            data: item?.data || {},
            itemStyle: {
              borderWidth: index === data?.length - 1 ? 2 : 1,
              borderStyle: 'solid',
              borderColor: '#FFFFFF',
              shadowColor: index === data?.length - 1 ? '#5086f3' : 'transparent',
              shadowBlur: index === data?.length - 1 ? 6 : 0,
            },
          })),
        },
      ],
    };
  };

  useEffect(() => {
    if (!Array.isArray(data)) return;
    if (!chart.current) return;
    chart.current.setOption(getOptionWithData(data));
  }, [data]);

  useEffect(() => {
    if (chartNode.current) {
      chart.current = echarts.init(chartNode.current);
      if (chart.current) {
        chart.current.setOption(getOptionWithData(data));
      }
    }

    return () => {
      if (chart.current) {
        chart.current.dispose();
      }
    };
  }, []);

  return <div ref={chartNode} style={{ width: '100%', height: '100%' }}></div>;
};

export default React.memo(AutoStackedAreaChart);
